'use client';

import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FiCode, FiServer, FiGlobe, FiDatabase, FiUser, FiMail, FiMapPin } from 'react-icons/fi';
import type { IconType } from 'react-icons';
import { AppLink, Tag } from '@/shared/ui/atoms';
import { SectionCard, QuoteBlock } from '@/shared/ui/molecules';
import { aboutMock } from '@/shared/store/mocks';
import type { AboutSkill } from '@/shared/store/mocks';
import styles from './about-section.module.scss';

gsap.registerPlugin(ScrollTrigger);

const ABOUT_ICONS: Record<AboutSkill['iconId'], IconType> = {
  code: FiCode,
  server: FiServer,
  globe: FiGlobe,
  database: FiDatabase,
};

export default function AboutSection() {
  const { title, subtitle, summaryCard, contactCard, skills, quote } = aboutMock;
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const quoteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          scrollTrigger: { trigger: titleRef.current, start: 'top 80%', toggleActions: 'play none none none' },
        }
      );
      gsap.fromTo(
        lineRef.current,
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 1,
          delay: 0.3,
          scrollTrigger: { trigger: lineRef.current, start: 'top 80%', toggleActions: 'play none none none' },
        }
      );
      const cards = cardsRef.current?.querySelectorAll(`.${styles.skillCard}`);
      if (cards?.length) {
        gsap.fromTo(
          cards,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            scrollTrigger: { trigger: cardsRef.current, start: 'top 85%', toggleActions: 'play none none none' },
          }
        );
      }
      gsap.fromTo(
        quoteRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          delay: 0.5,
          scrollTrigger: { trigger: quoteRef.current, start: 'top 85%', toggleActions: 'play none none none' },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section id="about" ref={sectionRef} className={styles.section}>
      <div className={styles.container}>
        <div ref={titleRef} className={styles.titleBlock}>
          <h2 className={styles.title}>{title}</h2>
          <div ref={lineRef} className={styles.titleLine} />
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <div className={styles.summaryGrid}>
          <SectionCard
            classNames={{ root: styles.card, hover: styles.cardHover, glow: styles.cardGlow, corner: styles.cardCorner }}
            showCorner
          >
            <div className={styles.cardIcon}>
              <FiUser size={32} />
            </div>
            <h3 className={styles.cardTitle}>{summaryCard.title}</h3>
            <p className={styles.cardText}>{summaryCard.text}</p>
          </SectionCard>
          <SectionCard
            classNames={{ root: styles.card, hover: styles.cardHover, glow: styles.cardGlow }}
          >
            <h3 className={styles.cardTitle}>{contactCard.title}</h3>
            <div className={styles.contactItem}>
              <FiMail size={20} />
              <AppLink href={`mailto:${contactCard.email}`}>{contactCard.email}</AppLink>
            </div>
            <div className={styles.contactItem}>
              <FiMapPin size={20} />
              <span>{contactCard.location}</span>
            </div>
          </SectionCard>
        </div>

        <div ref={cardsRef} className={styles.skillsGrid}>
          {skills.map((skill) => {
            const Icon = ABOUT_ICONS[skill.iconId];
            return (
              <SectionCard
                key={skill.title}
                classNames={{ root: styles.skillCard, hover: styles.skillCardHover, glow: styles.skillCardGlow }}
              >
                <div className={styles.skillIcon}>
                  <Icon size={32} />
                </div>
                <h3 className={styles.skillTitle}>{skill.title}</h3>
                <p className={styles.skillDesc}>{skill.description}</p>
                <div className={styles.techList}>
                  {skill.technologies.map((t) => (
                    <Tag key={t} className={styles.techTag}>{t}</Tag>
                  ))}
                </div>
              </SectionCard>
            );
          })}
        </div>

        <div ref={quoteRef}>
          <QuoteBlock
            text={quote.text}
            subtitle={quote.subtitle}
            classNames={{
              root: styles.quoteBlock,
              bg: styles.quoteBg,
              inner: styles.quoteInner,
              text: styles.quoteText,
              subtitle: styles.quoteSubtitle,
            }}
          />
        </div>
      </div>
    </section>
  );
}
